import { api, type FileInfo } from '@sd/contracts';
import { ClientModule, errorCode } from './context';
import { revokeObjectUrl } from './objectUrls';
import type { PlatformEvent } from './platform';

/**
 * An inline preview is the uploaded original (the server makes no thumbnails), so a larger image
 * shows as a file card and is only fetched when downloaded. 20 MiB holds phone photos and
 * screenshots, 50 megapixels a 48 MP phone camera's full-size photo, while a decompression bomb
 * (20000 × 20000 px is 1.6 GB decoded) stays out.
 */
export const INLINE_PREVIEW_MAX_BYTES = 20 * 1024 * 1024;
const INLINE_PREVIEW_MAX_PIXELS = 50_000_000;

/** Whether an image is small enough to fetch and decode for an inline preview (unknown dimensions go by size). */
export function fitsInlinePreview(file: Pick<FileInfo, 'size' | 'width' | 'height'>): boolean {
  if (file.size > INLINE_PREVIEW_MAX_BYTES) return false;
  return !file.width || !file.height || file.width * file.height <= INLINE_PREVIEW_MAX_PIXELS;
}

/**
 * Previews no longer shown that are kept for reuse; beyond this the least recently used is dropped
 * and its object URL revoked. A screen shows up to about a dozen image tiles plus the info panel's
 * three, so 24 covers going back to the last conversation or two without downloading them again,
 * while what stays in memory is bounded (each at most INLINE_PREVIEW_MAX_BYTES, usually a few MB).
 * Previews still on screen are never revoked: a long timeline mounts every image it has loaded.
 */
export const IDLE_PREVIEWS = 24;

/**
 * Previews fetched at once. A conversation of photos asked for every original together, each one slower on a phone
 * line; a few at a time, the ones asked for first (the tiles in view, the picture open in the viewer) arrive first.
 */
const PREVIEW_FETCHES = 3;

/** The server's small copies of an image: for a tile in a conversation, or for the photo viewer. */
export type PreviewSize = 'tile' | 'screen';
/** A file's original and its small copies are kept apart. */
const previewKey = (fileId: string, size?: PreviewSize) => (size ? `${fileId}:${size}` : fileId);

/** Attachment downloads through the host and inline image previews. */
export class Downloads extends ClientModule {
  /** Least recently used first; `users` counts the views showing it. */
  private readonly previews = new Map<string, { url: Promise<string>; users: number }>();
  private readonly downloadIds = new Map<string, string>(); // downloadId → fileId
  private fetching = 0;
  private readonly waiting: Array<() => void> = [];

  /** Runs `work` once fewer than PREVIEW_FETCHES previews are on their way; `first` goes ahead of the queue. */
  private async slot<T>(work: () => Promise<T>, first: boolean): Promise<T> {
    if (this.fetching >= PREVIEW_FETCHES) await new Promise<void>((resolve) => (first ? this.waiting.unshift(resolve) : this.waiting.push(resolve)));
    this.fetching += 1;
    try {
      return await work();
    } finally {
      this.fetching -= 1;
      this.waiting.shift()?.();
    }
  }

  async download(file: FileInfo, options: { acknowledgeUnscanned?: boolean } = {}): Promise<void> {
    const existing = this.state.downloads[file.id];
    if (existing?.state === 'done') {
      await this.ctx.platform.showInFolder(existing.downloadId);
      return;
    }
    if (existing?.state === 'busy') return;
    this.set((s) => ({ downloads: { ...s.downloads, [file.id]: { state: 'busy', downloadId: null, receivedBytes: 0, totalBytes: file.size } } }));
    try {
      const result = await this.ctx.platform.download({ serverId: this.ctx.serverId, fileId: file.id, fileName: file.name, ...(options.acknowledgeUnscanned ? { acknowledgeUnscanned: true } : {}) });
      if (result.status === 'browser') {
        this.set((s) => ({ downloads: { ...s.downloads, [file.id]: { state: 'browser' } } }));
      } else if (result.status === 'started') {
        this.downloadIds.set(result.downloadId, file.id);
        this.set((s) => ({ downloads: { ...s.downloads, [file.id]: { state: 'busy', downloadId: result.downloadId, receivedBytes: 0, totalBytes: file.size } } }));
      } else {
        this.set((s) => {
          const downloads = { ...s.downloads };
          delete downloads[file.id];
          return { downloads };
        });
      }
    } catch (error) {
      this.set((s) => ({ downloads: { ...s.downloads, [file.id]: { state: 'failed', reason: errorCode(error) } } }));
      this.ctx.notice({ kind: 'download.failed', fileId: file.id, name: file.name, code: errorCode(error) });
    }
  }

  onDownloadEvent(event: Extract<PlatformEvent, { type: 'download.progress' | 'download.done' | 'download.failed' }>) {
    const fileId = this.downloadIds.get(event.downloadId);
    if (!fileId) return;
    const name = this.findFileName(fileId);
    if (event.type === 'download.progress') {
      this.set((s) => ({ downloads: { ...s.downloads, [fileId]: { state: 'busy', downloadId: event.downloadId, receivedBytes: event.receivedBytes, totalBytes: event.totalBytes } } }));
    } else if (event.type === 'download.done') {
      this.set((s) => ({ downloads: { ...s.downloads, [fileId]: { state: 'done', downloadId: event.downloadId } } }));
      this.ctx.notice({ kind: 'download.done', fileId, name: event.fileName || name });
    } else {
      this.downloadIds.delete(event.downloadId);
      if (event.reason === 'canceled') {
        this.set((s) => {
          const downloads = { ...s.downloads };
          delete downloads[fileId];
          return { downloads };
        });
      } else {
        this.set((s) => ({ downloads: { ...s.downloads, [fileId]: { state: 'failed', reason: event.code ?? event.reason } } }));
        this.ctx.notice({ kind: 'download.failed', fileId, name, code: event.code });
      }
    }
  }

  private findFileName(fileId: string): string {
    for (const t of Object.values(this.state.timelines)) for (const m of t.messages) for (const a of m.attachments) if (a.id === fileId) return a.name;
    return '';
  }

  /** Cancels a download the app is saving; the main process removes the partial file. */
  async cancel(fileId: string): Promise<void> {
    const d = this.state.downloads[fileId];
    if (d?.state !== 'busy' || !d.downloadId) return;
    await this.ctx.platform.cancelDownload?.(d.downloadId);
  }

  async showInFolder(fileId: string): Promise<void> {
    const d = this.state.downloads[fileId];
    if (d?.state === 'done') await this.ctx.platform.showInFolder(d.downloadId);
  }

  /**
   * Object URL for an inline image preview (fetched with this session's credentials); `releasePreview` with the same
   * `size` once it is no longer shown. `first`: wanted now (the photo viewer), ahead of tiles waiting for their turn.
   * `size`: the server's small copy (`files.thumbnails`) for a tile or for the photo viewer's screen, else (an older
   * server, an animated image) the original.
   */
  previewUrl(fileId: string, options: { first?: boolean; size?: PreviewSize } = {}): Promise<string> {
    const key = previewKey(fileId, options.size);
    let preview = this.previews.get(key);
    if (preview) {
      this.previews.delete(key); // set again below as the most recently used
    } else {
      const original = () => this.api.call(api.files.preview, { params: { fileId } }).then((response) => response.blob());
      const size = options.size;
      const small = size && this.state.serverInfo?.capabilities.includes('files.thumbnails') === true;
      const fetchIt = small
        ? () =>
            this.api
              .call(api.files.thumbnail, { params: { fileId }, query: { size } })
              .then((response) => response.blob())
              .catch((error: unknown) => (errorCode(error) === 'NOT_FOUND' ? original() : Promise.reject(error)))
        : original;
      const url = this.slot(fetchIt, options.first === true).then((data) => URL.createObjectURL(data));
      const created = { url, users: 0 };
      url.catch(() => {
        if (this.previews.get(key) === created) this.previews.delete(key);
      });
      preview = created;
    }
    preview.users += 1;
    this.previews.set(key, preview);
    return preview.url;
  }

  /** A view stopped showing the preview: it is kept among the idle ones, and the least recently used beyond IDLE_PREVIEWS are revoked. */
  releasePreview(fileId: string, options: { size?: PreviewSize } = {}) {
    const key = previewKey(fileId, options.size);
    const preview = this.previews.get(key);
    if (!preview?.users) return;
    preview.users -= 1;
    if (preview.users) return;
    this.previews.delete(key);
    this.previews.set(key, preview);
    let idle = 0;
    for (const p of this.previews.values()) if (!p.users) idle += 1;
    for (const [id, p] of this.previews) {
      if (idle <= IDLE_PREVIEWS) break;
      if (p.users) continue;
      this.previews.delete(id);
      revokeObjectUrl(p.url);
      idle -= 1;
    }
  }

  revokePreviews() {
    for (const preview of this.previews.values()) revokeObjectUrl(preview.url);
    this.previews.clear();
  }
}
